import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '@/auth/useAuth';
import { useDragScroll } from '@/hooks/useDragScroll';
import { ArrowRightIcon, InfoIcon } from '@/components/ui/Icon';
import { InfoTray } from '@/components/ui/InfoTray';
import { Skeleton } from '@/components/ui/Skeleton';
import { TrendDelta } from '@/components/ui/TrendDelta';
import {
  DOMAIN_META,
  scoreBucket,
  trendDirection,
  type DomainKey,
  type DomainMeta,
} from '@/data/mockPerformance';
import type {
  CategoryScore,
  DomainRank,
  RecentActivityItem,
} from '@/api/dashboard';
import {
  useCategoryScores,
  useDomainRank,
  useOverallStats,
  useRecentActivity,
} from '@/hooks/api/useDashboard';
import { lensDomainPath } from '@/routes/paths';

import { PERIODS, usePeriod } from '@/state/PeriodContext';
import { PlanModal } from './PlanModal';
import styles from './DashboardPage.module.css';

interface DomainCard extends DomainMeta {
  score: number | null;
  trend: number | null;
  totalCharts: number | null;
  /** Provider rank within the peer cohort, 1-indexed. Null when API has no data yet. */
  rank: number | null;
  /** Cohort denominator. Null when API has no data yet. */
  cohortSize: number | null;
  /**
   * 'strict'   -> score is window-scoped.
   * 'all_time' -> requested window had no data, API widened to all-time.
   * 'none'     -> we never received a row for this domain.
   */
  windowFallback: 'strict' | 'all_time' | 'none';
}

/**
 * Home screen.
 *
 * Strict real-data policy: every visible value is sourced from Kai-API or
 * rendered with an intentional N/A treatment. Every domain tile with data
 * links to Lens with that domain's tab active (PLA-1019); the sub-domain
 * trend drill-down now lives behind the Trends button on Lens (PLA-1020).
 */
export function DashboardPage() {
  const { user } = useAuth();
  const { period, periodMeta, setPeriod } = usePeriod();
  const [planOpen, setPlanOpen] = useState(false);
  // Whether the "how these numbers are calculated" tray is open.
  const [statInfo, setStatInfo] = useState(false);

  const filters = { dateRange: periodMeta.api };

  const scoresQuery     = useCategoryScores(filters);
  const overallQuery    = useOverallStats(filters);
  // Windowed peer rank for the RSQ tile (GET /rank/rsq). Every provider's
  // score is computed over the ACTIVE period with the same formula as the
  // score, and this NPI is ranked within that list — same methodology as
  // the Lens complaint rank, so the chip follows the period filter instead
  // of the old fixed 30/60/90/180-day aggregates. FFS/VBC tiles will get
  // their own useDomainRank calls when those domains carry data.
  const rsqRankQuery    = useDomainRank('rsq', filters);
  // Trend-based activity feed (PLA-480) — capped at 5 items per the spec.
  // The API ranks alerts > stats > trends and returns at most 5, so
  // limit: 5 here matches both the spec and the server-side LIMIT.
  const activityQuery   = useRecentActivity({ ...filters, limit: 5 });

  const firstName = useMemo(() => {
    if (!user) return '';
    return user.name.split(/\s+/)[0] ?? user.name;
  }, [user]);

  const domainCards = useMemo(
    () => hydrateDomains(DOMAIN_META, scoresQuery.data, { rsq: rsqRankQuery.data }),
    [scoresQuery.data, rsqRankQuery.data],
  );

  const overall = overallQuery.data;
  const totalVisits = overall && overall.total_visits > 0 ? overall.total_visits : null;
  const chartsAnalyzed = overall && overall.total_charts > 0 ? overall.total_charts : null;

  // Hooks must run unconditionally on every render, so these stay above the
  // early return below (react-hooks/rules-of-hooks).
  const filtersRef = useRef<HTMLDivElement>(null);
  useDragScroll(filtersRef);

  if (!user) return null;

  const isHeroLoading = scoresQuery.isLoading || overallQuery.isLoading;
  const isHeroError = scoresQuery.isError || overallQuery.isError;
  const rsqRow = domainCards.find((d) => d.key === 'rsq');
  // Hide domains with no score (e.g. FFS/VBC/IPA before their data lands).
  const visibleDomains = domainCards.filter((d) => d.score != null);
  const rsqIsFallback = rsqRow?.windowFallback === 'all_time';

  return (
    <section className={styles.page} aria-labelledby="dashboard-greeting">
      <header className={styles.welcome}>
        <span className={styles.accent} aria-hidden="true" />
        <div>
          <h1 id="dashboard-greeting" className={styles.greeting}>
            Welcome, <span className={styles.name}>{firstName}</span>
          </h1>
          <p className={styles.date}>{formatDate(new Date())}</p>
        </div>
      </header>

      <div ref={filtersRef} className={styles.filters} role="group" aria-label="Time period">
        {PERIODS.map((p) => (
          <button
            key={p.value}
            type="button"
            className={p.value === period ? `${styles.filter} ${styles.filterActive}` : styles.filter}
            onClick={() => setPeriod(p.value)}
            aria-pressed={p.value === period}
          >
            {p.label}
          </button>
        ))}
      </div>

      <article className={styles.hero}>
        <div className={styles.heroTop}>
          <div>
            <h2 className={styles.heroTitle}>My Performance</h2>
            <p className={styles.heroSub}>{periodMeta.label}</p>
          </div>
          {/* One explainer for the whole card (visits, charts, RSQ, goal,
              rank) instead of a button per stat. */}
          <button
            type="button"
            className={styles.heroInfoBtn}
            aria-label="How these numbers are calculated"
            onClick={() => setStatInfo(true)}
          >
            <InfoIcon width={14} height={14} />
          </button>
        </div>

        {isHeroError && (
          <div className={styles.errorBanner} role="status">
            Couldn't reach Kai-API. Some values are unavailable.
          </div>
        )}
        {!isHeroError && rsqIsFallback && (
          <div className={styles.infoBanner} role="status">
            No RSQ® activity in this window. Showing the all-time score for context.
          </div>
        )}

        <div className={styles.counters}>
          <div className={styles.counter}>
            <span className={styles.counterLabel}>Total Visits</span>
            {overallQuery.isLoading ? (
              <Skeleton variant="text" width={56} height={20} />
            ) : totalVisits != null ? (
              <span className={styles.counterValue}>{totalVisits.toLocaleString()}</span>
            ) : (
              <>
                <span className={`${styles.counterValue} ${styles.counterMuted}`}>N/A</span>
                <span className={styles.counterHint}>no source yet</span>
              </>
            )}
          </div>
          <div className={styles.counter}>
            <span className={styles.counterLabel}>Charts Analyzed</span>
            {overallQuery.isLoading ? (
              <Skeleton variant="text" width={56} height={20} />
            ) : chartsAnalyzed != null ? (
              <span className={styles.counterValue}>{chartsAnalyzed.toLocaleString()}</span>
            ) : (
              <>
                <span className={`${styles.counterValue} ${styles.counterMuted}`}>N/A</span>
                <span className={styles.counterHint}>awaiting data</span>
              </>
            )}
          </div>
        </div>

        {/* Only domains with data render; the grid sizes itself to the
            visible count (1-4) so the remaining tiles fill the row. While
            loading we show a single placeholder since the count is unknown. */}
        {(isHeroLoading || visibleDomains.length > 0) && (
          <div
            className={styles.domainGrid}
            data-count={isHeroLoading ? 1 : visibleDomains.length}
          >
            {isHeroLoading ? (
              <div className={styles.domainMini} aria-hidden="true">
                <Skeleton variant="text" width={48} />
                <Skeleton variant="text" width={56} height={22} />
                <Skeleton variant="text" width={36} />
                <Skeleton variant="text" width={64} />
              </div>
            ) : (
              visibleDomains.map((domain) => (
                <DomainMiniCard key={domain.key} domain={domain} />
              ))
            )}
          </div>
        )}

        <button
          type="button"
          className={styles.planButton}
          onClick={() => setPlanOpen(true)}
        >
          View My Plan <ArrowRightIcon width={16} height={16} />
        </button>
      </article>

      <article className={styles.card} aria-labelledby="recent-activity-heading">
        <header className={styles.cardHeader}>
          <span className={styles.accent} aria-hidden="true" />
          <h2 id="recent-activity-heading" className={styles.cardTitle}>
            Recent Activity
          </h2>
          {(() => {
            // Quick "2 alerts · 3 wins" summary so the user can read the
            // emotional tone of the feed at a glance. Derived from items
            // (counts aren't on the API response payload).
            const items = activityQuery.data ?? [];
            if (items.length === 0) return null;
            const alerts = items.filter((it) => it.type === 'alert').length;
            const positives = items.length - alerts;
            const parts: string[] = [];
            if (alerts > 0) parts.push(`${alerts} alert${alerts === 1 ? '' : 's'}`);
            if (positives > 0) parts.push(`${positives} win${positives === 1 ? '' : 's'}`);
            return (
              <span className={styles.cardHeaderMeta}>
                {parts.join(' · ')}
              </span>
            );
          })()}
        </header>
        <RecentActivity
          isLoading={activityQuery.isLoading}
          isError={activityQuery.isError}
          items={activityQuery.data ?? []}
        />
      </article>

      <PlanModal
        open={planOpen}
        onClose={() => setPlanOpen(false)}
        filters={filters}
        npi={user?.npi ?? null}
      />

      {/* One explainer for the whole My Performance card. Counter wording
          tracks /app/overall-stats (total_visits = distinct chart_id in the
          window; total_charts = distinct chart_id with include_in_denom =
          true); RSQ wording tracks services/rsqScoring. */}
      <InfoTray
        open={statInfo}
        onClose={() => setStatInfo(false)}
        eyebrow="My Performance"
        title="How these numbers are calculated"
      >
        <div className={styles.statInfo}>
          <section>
            <h4 className={styles.statInfoHeading}>Total Visits</h4>
            <p>
              Every patient chart attributed to you in the selected period that
              Kai received and processed. Each chart is counted once, whether or
              not it ended up contributing to your RSQ® score.
            </p>
          </section>
          <section>
            <h4 className={styles.statInfoHeading}>Charts Analyzed</h4>
            <p>
              The subset of those visits where at least one RSQ® indicator
              applied and could be measured. These are the charts that
              contribute to your score. A visit is counted but not analyzed when
              its complaint is outside the RSQ program or the chart lacked
              enough documented detail to evaluate.
            </p>
          </section>
          <section>
            <h4 className={styles.statInfoHeading}>RSQ® score</h4>
            <p>
              Risk, Safety, Quality. The percentage of applicable indicators that
              were documented across your analyzed charts in the period. Higher
              is better. The tick mark on the bar is your organization's goal;
              the bar turns green once you meet it.
            </p>
          </section>
          <section>
            <h4 className={styles.statInfoHeading}>Rank</h4>
            <p>
              Your position among providers in your organization, scored over the
              same period with the same method. #1 is the highest RSQ® score.
            </p>
          </section>
          <p className={styles.statInfoNote}>
            Everything on this card follows the period selected at the top of
            the page. Tap the RSQ® tile to open Lens for the breakdown by chief
            complaint and indicator.
          </p>
        </div>
      </InfoTray>
    </section>
  );
}

/* DomainMiniCard
 * --------------------------------------------------------------------- */

interface DomainMiniCardProps {
  domain: DomainCard;
}

const DomainMiniCard = ({ domain }: DomainMiniCardProps) => {
  const hasScore = domain.score != null;
  const bucket = scoreBucket(domain.score);
  const direction = trendDirection(domain.trend);

  const measuredLabel =
    domain.totalCharts != null && domain.totalCharts > 0
      ? `${domain.totalCharts.toLocaleString()} charts`
      : null;

  // Any domain with data: link to Lens with that domain's tab active
  // (PLA-1019). RSQ previously opened the trend modal here; that drill-down
  // now lives behind the Trends button on Lens (PLA-1020).
  if (hasScore) {
    return (
      <Link
        to={lensDomainPath(domain.key)}
        className={styles.domainMini}
        aria-label={`Open ${domain.fullName} in Lens; current score ${domain.score}%${measuredLabel ? `, ${measuredLabel}` : ''}`}
      >
        <span className={styles.domainLabel}>{domain.label}</span>
        <span className={`${styles.domainScore} ${styles[`score_${bucket}`]}`}>
          {domain.score}%
        </span>
        {/* Only show a trend when there's an actual move; a flat "0" or
            missing value is just noise on the tile. */}
        {(direction === 'up' || direction === 'down') && (
          <TrendDelta
            className={styles.domainTrend}
            delta={domain.trend}
            direction={direction}
            unit="pts"
            title={`${direction === 'up' ? 'Up' : 'Down'} ${Math.abs(domain.trend ?? 0).toFixed(1)} points vs the prior period`}
          />
        )}
        {/* Rank + chart count travel together so the single-tile layout can
            center them as one block against the score. `display: contents`
            in the stacked layout keeps them flowing as before. */}
        <span className={styles.domainSide}>
          <RankChip rank={domain.rank} cohortSize={domain.cohortSize} />
          {measuredLabel && <span className={styles.domainMeasured}>{measuredLabel}</span>}
        </span>
        {/* Disclosure chevron: the standard "this card navigates" cue. The
            Link's aria-label already names the destination. */}
        <span className={styles.domainGo} aria-hidden="true">
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 3.5 10.5 8 6 12.5" />
          </svg>
        </span>
      </Link>
    );
  }

  // No data yet: dashed-ghost card, no interaction.
  return (
    <div
      className={`${styles.domainMini} ${styles.domainMiniEmpty}`}
      aria-label={`${domain.fullName}: no data yet`}
    >
      <span className={styles.domainLabel}>{domain.label}</span>
      <span className={`${styles.domainScore} ${styles.score_unavailable}`}>N/A</span>
      <span className={styles.domainHint}>data pending</span>
      <RankChip rank={null} cohortSize={null} />
    </div>
  );
};

/* RankChip — small pill showing the provider's rank within the peer cohort.
 *
 * Renders `#4 of 20` when both rank and cohort size are known. Falls back to a
 * muted, dashed ghost pill labelled "Rank —" when the API hasn't exposed
 * ranking yet (tracked in PLA-445 Tier 2). The chip itself stays visually
 * consistent across states so the layout doesn't shift once data arrives.
 * --------------------------------------------------------------------- */
interface RankChipProps {
  rank: number | null;
  cohortSize: number | null;
}

const RankChip = ({ rank, cohortSize }: RankChipProps) => {
  const hasRank = rank != null && cohortSize != null && cohortSize > 0;
  const chipClass = hasRank
    ? `${styles.rankChip} ${styles.rankChipReady}`
    : `${styles.rankChip} ${styles.rankChipEmpty}`;
  return (
    <span
      className={chipClass}
      aria-label={
        hasRank
          ? `Rank ${rank} of ${cohortSize} in your peer cohort`
          : 'Peer rank not available yet'
      }
    >
      {hasRank ? (
        <>
          <span className={styles.rankHash} aria-hidden="true">#</span>
          {rank}
          <span className={styles.rankOf} aria-hidden="true"> of </span>
          {cohortSize}
        </>
      ) : (
        <>
          <span className={styles.rankHash} aria-hidden="true">#</span>
          <span className={styles.rankPlaceholder}>—</span>
        </>
      )}
    </span>
  );
};

/* RecentActivity
 * --------------------------------------------------------------------- */

interface RecentActivityProps {
  isLoading: boolean;
  isError: boolean;
  items: RecentActivityItem[];
}

const RecentActivity = ({ isLoading, isError, items }: RecentActivityProps) => {
  if (isLoading) {
    return (
      <ul className={styles.activityList} aria-busy="true">
        {Array.from({ length: 4 }).map((_, i) => (
          <li key={i} className={styles.activityItem}>
            <Skeleton variant="block" width={32} height={32} />
            <div style={{ flex: 1 }}>
              <Skeleton variant="text" width="80%" />
              <Skeleton variant="text" width="30%" />
            </div>
          </li>
        ))}
      </ul>
    );
  }

  if (isError) {
    return (
      <div className={styles.activityPlaceholder}>
        <strong>Activity feed unavailable</strong>
        <p>Couldn't reach Kai-API. Try again in a moment.</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className={styles.activityPlaceholder}>
        <strong>No activity yet</strong>
        <p>Once charts start flowing through the validation pipeline, recent events will land here.</p>
      </div>
    );
  }

  return (
    <ul className={styles.activityList}>
      {items.map((event) => {
        // Tone drives the entire row (icon bg, gradient backdrop, chip
        // color) so the visual identity is consistent across types.
        // Alert = coral, Stat = teal, Trend = green.
        const tone =
          event.type === 'alert'
            ? styles.activity_alert
            : event.type === 'stat'
              ? styles.activity_stat
              : styles.activity_trend;
        const label =
          event.type === 'alert'
            ? 'Alert'
            : event.type === 'stat'
              ? 'Stat'
              : 'Trend';
        // `label` is intentionally unused: the icon + tone already convey
        // the type, so the chip just added visual noise.
        void label;
        return (
          <li key={event.id} className={`${styles.activityItem} ${tone}`}>
            <span className={`${styles.activityIcon} ${tone}`} aria-hidden="true">
              <span className={styles.activityEmoji}>{event.icon}</span>
            </span>
            <div className={styles.activityBody}>
              <h3 className={styles.activityTitle}>{event.title}</h3>
              <p className={styles.activityText}>{event.message}</p>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

/* Pure helpers
 * --------------------------------------------------------------------- */

/**
 * Long-form date for the welcome header, e.g. "Wednesday, June 3, 2026".
 */
const formatDate = (d: Date): string =>
  d.toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

/**
 * Render the trend delta as a signed value plus arrow. Null means no data
 * yet (renders an em dash); 0 means data exists but no movement.
 */
const KNOWN_DOMAIN_KEYS = new Set<DomainKey>(['rsq', 'ffs', 'vbc', 'ipa']);
const isKnownDomainKey = (value: string): value is DomainKey =>
  KNOWN_DOMAIN_KEYS.has(value as DomainKey);

const hydrateDomains = (
  meta: DomainMeta[],
  apiData?: CategoryScore[],
  /**
   * Windowed peer rank per domain from GET /rank/:domain. When a domain has
   * an entry here (even `undefined` while its query is in flight), its
   * rank/cohort come EXCLUSIVELY from that endpoint — the legacy
   * category-scores rank is ignored so the chip always reflects the
   * score-based, period-following methodology.
   */
  rankByDomain?: Partial<Record<DomainKey, DomainRank | undefined>>,
): DomainCard[] => {
  const byKey = new Map<DomainKey, CategoryScore>();
  if (apiData) {
    for (const row of apiData) {
      const key = row.id as DomainKey;
      if (isKnownDomainKey(key)) byKey.set(key, row);
    }
  }

  return meta.map((m) => {
    const row = byKey.get(m.key);
    const hasRankOverride = rankByDomain ? m.key in rankByDomain : false;
    const override = rankByDomain?.[m.key];
    if (!row) {
      return {
        ...m,
        score: null,
        trend: null,
        totalCharts: null,
        rank: null,
        cohortSize: null,
        windowFallback: 'none' as const,
      };
    }
    const hasData = (row.totalCharts ?? 0) > 0;
    const trend =
      hasData && row.trend !== 'neutral'
        ? row.change
        : hasData ? 0 : null;
    return {
      ...m,
      score: hasData ? row.score : null,
      trend,
      totalCharts: row.totalCharts ?? null,
      rank: hasRankOverride ? override?.rank ?? null : row.rank ?? null,
      cohortSize: hasRankOverride ? override?.peerCount ?? null : row.cohortSize ?? null,
      windowFallback: (row.windowFallback ?? 'strict') as 'strict' | 'all_time' | 'none',
    };
  });
};
